import { useEffect, useRef, useCallback } from 'react';
import { getToken, getTokenExp, getAuthHeaders } from '../utils/auth';
import { endSession as endSessionAndReload, markActivity, getSharedLastActivity } from '../utils/session';
import { fetchNumberSetting, SETTING_SESSION_IDLE_MINUTES } from '../utils/settings';
import { refreshAccessToken } from '../utils/tokenRefresh';

/**
 * #259 Oturum zaman aşımı bekçisi.
 *
 * Talep: "Sabahtan login oldum, hiç kesilmiyorum — güvenlik açığı." Back Office'te
 * oturum ne hareketsizlikte ne de token süresi dolduğunda kapanıyordu. Bu bileşen
 * yalnız kimlik doğrulanmış düzen (Layout) içinde monte edilir ve şu kapıları işletir:
 *
 *  1. Hareketsizlik (idle): kullanıcı IDLE_LIMIT_MS boyunca hiçbir etkileşim yapmazsa
 *     oturum kapanır. Terk edilmiş açık oturumu (masabaşından kalkma, akşam kapatmama)
 *     kapatır — dizüstü uyku/kapatmada timer dursa bile uyanışta fark tespit edilir.
 *     Hareketsizlik SEKMEDE DEĞİL OTURUM GENELİNDE sayılır (#850): etkileşim
 *     localStorage üzerinden tüm sekmelerle paylaşılır, böylece bir sekmede
 *     çalışırken boştaki başka bir sekme oturumu kapatamaz. Ayrıntı: utils/session.
 *  2. Önleyici yenileme: kullanıcı ÇALIŞIRKEN "exp" yaklaşıyorsa token sessizce
 *     yenilenir (bkz. utils/tokenRefresh). Bu olmadan aktif kullanıcı, erişim
 *     token'ının ömrü dolduğu anda ekranın ortasında login'e atılıyordu —
 *     "sebepsiz atılma" şikâyetinin ana kaynağı buydu.
 *  3. Mutlak süre (exp): yenileme mümkün değilse ve "exp" anına ulaşıldıysa oturum
 *     kapanır (arka planda API zaten 401 döndürür; burada temiz biçimde login'e alırız).
 *
 * Hareketsizlik süresi Genel Parametreler ekranından parametriktir
 * (Setting "Session.IdleTimeoutMinutes", dakika). Ayar yoksa/geçersizse
 * varsayılan DEFAULT_IDLE_MINUTES kullanılır; en az 1 dk'ya kısıtlanır
 * (yanlış konfigürasyonla anında atılmayı önler).
 *
 * Görsel çıktı yoktur (null render); yalnız yan etki (dinleyici + interval) kurar.
 */

// Ayar okunamazsa/tanımsızsa varsayılan hareketsizlik süresi (dk).
const DEFAULT_IDLE_MINUTES = 30;
// Kontrol sıklığı — kısa tutmak gecikmeyi azaltır, maliyeti önemsizdir.
const CHECK_INTERVAL_MS = 30 * 1000;
// "exp"e bu kadar kala token yenilenir. Tick aralığının birkaç katı olmalı ki
// yenileme yavaş bir ağda bile süre dolmadan tamamlanabilsin.
const RENEW_BEFORE_MS = 2 * 60 * 1000;

export function SessionGuard() {
  const lastActivity = useRef(Date.now());
  // Interval closure'ı her tick'te güncel limiti okusun diye ref'te tutulur.
  const idleLimitRef = useRef(DEFAULT_IDLE_MINUTES * 60 * 1000);
  // Teşhis: sayacı en son hangi etkileşim sıfırladı ve tick'ler arasında ne kadar
  // zaman geçti. Uykuya alınan/arka plana atılan sekmede tick'ler kısıldığı için
  // "hareketsiz değildim ama atıldım" şikâyetleri ancak bu iki alanla ayrılabiliyor.
  const lastEvent = useRef('init');
  const lastTick = useRef(Date.now());
  // Yenileme uçuştayken sonraki tick'in ikinci bir tur başlatmasını engeller.
  const renewing = useRef(false);

  // Temizlik + login'e dönüş utils/session'ın işi; burada yalnız "ne zaman" kararı verilir.
  const endSession = useCallback((reason, detail) => {
    if (!getToken()) return; // zaten çıkış yapılmış
    endSessionAndReload(reason, detail);
  }, []);

  useEffect(() => {
    // Genel Parametreler'den hareketsizlik süresini (dk) oku; en az 1 dk.
    let active = true;
    fetchNumberSetting(SETTING_SESSION_IDLE_MINUTES, getAuthHeaders(), DEFAULT_IDLE_MINUTES)
      .then((minutes) => {
        if (!active) return;
        const m = Number.isFinite(minutes) && minutes >= 1 ? minutes : DEFAULT_IDLE_MINUTES;
        idleLimitRef.current = m * 60 * 1000;
      });

    // Herhangi bir kullanıcı etkileşimi hareketsizlik sayacını sıfırlar. Sayaç
    // hem yerelde (bu sekme) hem de paylaşımlı olarak (diğer sekmeler) güncellenir;
    // markActivity kendi içinde throttle'lıdır, mousemove seli storage'ı yormaz.
    const bump = (e) => {
      const now = Date.now();
      lastActivity.current = now;
      lastEvent.current = e?.type ?? 'unknown';
      markActivity(now);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    // Sekmenin açılması da bir etkileşimdir: diğer sekmeler bu oturumu boş sanmasın.
    markActivity();

    const tick = () => {
      if (!getToken()) return; // guard yalnız oturum açıkken çalışır
      const now = Date.now();
      // İki tick arası beklenenden çok uzunsa sekme arka plandaydı ya da makine uyudu;
      // bu durumda "hareketsizlik" gerçek bir terk edilmişlik olmayabilir. Sebep
      // günlüğünde (utils/sessionLog) bu ayrımı yapabilmek için her kayda geçer.
      const tickGapMs = now - lastTick.current;
      lastTick.current = now;

      // Hareketsizlik ÖNCE bakılır: masasından kalkmış kullanıcının token'ını yenilemek,
      // kapatmak istediğimiz terk edilmiş oturumu sonsuza dek açık tutmak olurdu.
      //
      // #850: ölçüt bu sekmenin değil, OTURUMUN son etkileşimi. Kullanıcı başka bir
      // sekmede çalışıyorsa bu sekme boş olsa da oturum boş değildir; en yenisi alınır.
      const sharedLastActivity = getSharedLastActivity();
      const lastActivityMs = Math.max(lastActivity.current, sharedLastActivity);
      const idleForMs = now - lastActivityMs;
      if (idleForMs >= idleLimitRef.current) {
        endSession('idle', {
          idleForSec: Math.round(idleForMs / 1000),
          idleLimitMin: Math.round(idleLimitRef.current / 60000),
          lastEvent: lastEvent.current,
          tickGapMs,
          // Teşhis: çıkışa bu sekmenin mi yoksa paylaşımlı sayacın mı karar verdiği.
          // Sahada "başka sekmede çalışıyordum" iddiasını ancak bu ayırt eder.
          localIdleSec: Math.round((now - lastActivity.current) / 1000),
          sharedIdleSec: sharedLastActivity ? Math.round((now - sharedLastActivity) / 1000) : null,
        });
        return;
      }

      const exp = getTokenExp();
      if (exp == null) return; // "exp"siz token: mutlak süre kapısı işlemez
      const remaining = exp - now;

      if (remaining > RENEW_BEFORE_MS || renewing.current) return;

      if (remaining <= 0) {
        // Süre çoktan dolmuş (ör. dizüstü uykudan yeni uyandı). Yenileme yine de
        // denenir: refresh token günlerce geçerlidir, erişim token'ının ölmüş olması
        // oturumun bittiği anlamına gelmez. Burada — aşağıdaki daldan farklı olarak —
        // geçici hata da çıkışa götürür: elde çalışan bir token KALMAMIŞTIR, kullanıcıyı
        // her isteği 401 dönen bir ekranda tutmanın anlamı yok.
        renewing.current = true;
        refreshAccessToken()
          .then(({ token }) => {
            if (!token) {
              endSession('expired', {
                phase: 'renew-after-expiry',
                overdueSec: Math.round(-remaining / 1000),
                tickGapMs,
              });
            }
          })
          .finally(() => { renewing.current = false; });
        return;
      }

      // Süre yaklaşıyor ve kullanıcı aktif: sessizce yenile. Reddedilirse oturum
      // gerçekten bitmiştir; geçici hatada (ağ/429) dokunma — sonraki tick tekrar dener,
      // süre dolarsa yukarıdaki dal devreye girer.
      renewing.current = true;
      refreshAccessToken()
        .then(({ token, rejected }) => {
          if (!token && rejected) {
            endSession('expired', {
              phase: 'renew-rejected',
              remainingSec: Math.round(remaining / 1000),
              tickGapMs,
            });
          }
        })
        .finally(() => { renewing.current = false; });
    };

    const intervalId = setInterval(tick, CHECK_INTERVAL_MS);
    // Sekme yeniden görünür olunca anında kontrol (arka planda interval kısılabilir/uyku).
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [endSession]);

  return null;
}

export default SessionGuard;
